/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Collapse,
  Divider,
  Drawer,
  Input,
  InputNumber,
  Row,
  Switch,
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { ReactComponent as EditIcon } from '../../../assets/svg/pen-to-square-solid.svg';
import JsonCodeEditor from './JsonCodeEditor';

const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getSectionItemTitle = (key) =>
  humanizeKey(
    String(key)
      .replace(/(_list|_items|List|Items)$/, '')
      .replace(/s$/, ''),
  );

const isUrlKey = (key) => /image|icon|logo|url|thumbnail|banner/i.test(key);

const isLongTextKey = (key) =>
  /description|content|body|note|summary/i.test(key);

const isOrderKey = (key) => /order/i.test(key);

const isPlainObject = (val) =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

const isBranch = (val) => val !== null && typeof val === 'object';

const isEmptyValue = (val) =>
  val === null ||
  val === undefined ||
  (typeof val === 'string' && val.trim() === '');

const getFieldType = (key, val, hint) => {
  const typed = isEmptyValue(val) && !isEmptyValue(hint) ? hint : val;
  if (typeof typed === 'boolean') return 'switch';
  if (typeof typed === 'number') return 'number';
  if (typed === null || isOrderKey(key)) return 'number';
  if (isUrlKey(key)) return 'url';
  if (isLongTextKey(key)) return 'textarea';
  return 'text';
};

const getFieldLabel = (key) => humanizeKey(key);

const describeSyntaxError = (draft, error) => {
  const reason = String(error?.message ?? 'Invalid JSON')
    .replace(/\s*in JSON at position \d+/, '')
    .replace(/^JSON\.parse:\s*/, '');
  const position = /position (\d+)/.exec(error?.message ?? '');
  if (!position) {
    return `Invalid JSON: ${reason}.`;
  }
  const upto = draft.slice(0, Number(position[1]));
  const line = upto.split('\n').length;
  const column = upto.length - upto.lastIndexOf('\n');
  return `Invalid JSON at line ${line}, column ${column}: ${reason}.`;
};

const validateJsonDraft = (draft) => {
  if (!draft || draft.trim() === '') {
    return { error: 'Please enter the configuration JSON.' };
  }
  let parsed = null;
  try {
    parsed = JSON.parse(draft);
  } catch (err) {
    return { error: describeSyntaxError(draft, err) };
  }
  if (Array.isArray(parsed)) {
    return {
      error: 'The configuration must be a JSON object ({ ... }), not an array.',
    };
  }
  if (parsed === null || typeof parsed !== 'object') {
    return {
      error: `The configuration must be a JSON object ({ ... }), not a ${
        parsed === null ? 'null' : typeof parsed
      } value.`,
    };
  }
  if (Object.keys(parsed).length === 0) {
    return { error: 'The configuration is empty, it needs at least one key.' };
  }
  const badKey = Object.keys(parsed).find(
    (key) => key.trim() === '' || key !== key.trim(),
  );
  if (badKey !== undefined) {
    return { error: 'Keys cannot be blank or padded with spaces.' };
  }
  return { parsed };
};

const isInvalidUrl = (url) => {
  if (isEmptyValue(url)) return false;
  const regex = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg|avif|bmp|ico|tiff?|jfif)(\?.*)?$/i;
  return !regex.test(String(url).trim());
};

const rankKey = (val) => {
  if (Array.isArray(val)) return 4;
  if (isPlainObject(val)) return 3;
  if (typeof val === 'boolean') return 1;
  return 2;
};

const orderedKeys = (sample, keys) =>
  (keys ?? Object.keys(sample))
    .slice()
    .sort((a, b) => rankKey(sample[a]) - rankKey(sample[b]));

const isEmptyBranch = (val) =>
  (Array.isArray(val) && val.length === 0) ||
  (isPlainObject(val) && Object.keys(val).length === 0);

const buildUnionSample = (list) => {
  const sample = {};
  list.forEach((item) => {
    if (!isPlainObject(item)) return;
    Object.keys(item).forEach((key) => {
      const current = sample[key];
      const next = item[key];
      const isBetter =
        !(key in sample) ||
        current === undefined ||
        current === null ||
        (isEmptyBranch(current) && !isEmptyBranch(next));
      if (isBetter) {
        sample[key] = next;
      }
    });
  });
  return sample;
};

const blankValueFor = (sample) => {
  if (typeof sample === 'boolean') return true;
  if (typeof sample === 'number') return null;
  if (Array.isArray(sample)) {
    if (sample.length === 0) return [];
    const entrySample =
      sample.find((entry) => !isEmptyBranch(entry)) ?? sample[0];
    return [blankValueFor(entrySample)];
  }
  if (isPlainObject(sample)) {
    const blank = {};
    Object.keys(sample).forEach((key) => {
      blank[key] = blankValueFor(sample[key]);
    });
    return blank;
  }
  return '';
};

const buildItemTemplate = (list) => {
  const sample = buildUnionSample(list);
  if (Object.keys(sample).length > 0) {
    return blankValueFor(sample);
  }
  if (list.length > 0) {
    return blankValueFor(list[0]);
  }
  return null;
};

const setValueAtPath = (target, path, val) => {
  if (path.length === 0) return val;
  const [head, ...rest] = path;
  if (Array.isArray(target)) {
    const copy = [...target];
    copy[head] = setValueAtPath(copy[head], rest, val);
    return copy;
  }
  return { ...target, [head]: setValueAtPath(target?.[head], rest, val) };
};

const removeValueAtPath = (target, path) => {
  const [head, ...rest] = path;
  if (rest.length === 0) {
    if (Array.isArray(target)) return target.filter((_, idx) => idx !== head);
    const copy = { ...target };
    delete copy[head];
    return copy;
  }
  if (Array.isArray(target)) {
    const copy = [...target];
    copy[head] = removeValueAtPath(copy[head], rest);
    return copy;
  }
  return { ...target, [head]: removeValueAtPath(target[head], rest) };
};

const emptyLikeSample = (sampleVal) => {
  if (typeof sampleVal === 'boolean') return false;
  if (typeof sampleVal === 'number') return null;
  if (Array.isArray(sampleVal)) return [];
  if (isPlainObject(sampleVal)) {
    const blank = {};
    Object.keys(sampleVal).forEach((key) => {
      blank[key] = emptyLikeSample(sampleVal[key]);
    });
    return blank;
  }
  return '';
};

const itemValueFor = (item, key, sample) =>
  key in (item ?? {}) ? item[key] : emptyLikeSample(sample?.[key]);

const collectLeafFields = (val, key, list, hint, out = []) => {
  if (isPlainObject(val)) {
    Object.keys(val).forEach((childKey) =>
      collectLeafFields(val[childKey], childKey, list, undefined, out),
    );
    return out;
  }
  if (Array.isArray(val)) {
    const sample = buildUnionSample(val);
    val.forEach((item) => {
      if (!isPlainObject(item)) {
        collectLeafFields(item, key, val, val[0], out);
        return;
      }
      new Set([
        ...Object.keys(sample),
        ...Object.keys(item),
      ]).forEach((childKey) =>
        collectLeafFields(
          itemValueFor(item, childKey, sample),
          childKey,
          val,
          sample[childKey],
          out,
        ),
      );
    });
    return out;
  }
  out.push({ key, value: val, list, hint });
  return out;
};

const isRequiredField = (key, val, hint) =>
  getFieldType(key, val, hint) !== 'switch';

const isDuplicateInList = (list, key, val) => {
  if (!Array.isArray(list) || isEmptyValue(val)) return false;
  return (
    list.filter((item) => isPlainObject(item) && item[key] === val).length > 1
  );
};

const getFieldError = (key, val, list, hint) => {
  if (isRequiredField(key, val, hint) && isEmptyValue(val)) {
    return { hasError: true, text: null };
  }
  if (getFieldType(key, val, hint) === 'url' && isInvalidUrl(val)) {
    return { hasError: true, text: 'Invalid image URL.' };
  }
  if (isOrderKey(key) && isDuplicateInList(list, key, val)) {
    return { hasError: true, text: null };
  }
  return { hasError: false, text: null };
};

const CollapsibleBox = ({
  title,
  extra,
  invalid,
  background,
  style,
  defaultOpen,
  children,
}) => {
  const boxRef = useRef(null);

  useEffect(() => {
    if (defaultOpen && boxRef.current) {
      boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [defaultOpen]);

  return (
    <div ref={boxRef}>
      <Collapse
        className="ui-config-collapse"
        size="small"
        defaultActiveKey={defaultOpen ? ['panel'] : undefined}
        expandIconPosition="start"
        style={{
          background,
          borderRadius: '6px',
          border: invalid ? '1px solid #ff4d4f' : '1px solid #e8e8e8',
          ...style,
        }}
      >
        <Collapse.Panel
          key="panel"
          header={
            <span style={{ fontWeight: '600', fontSize: '13px' }}>{title}</span>
          }
          extra={extra}
        >
          {children}
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

const labelStyle = {
  marginBottom: '4px',
  fontSize: '12px',
  fontWeight: '500',
  color: '#595959',
};

const LeafField = ({
  fieldKey,
  value,
  hint,
  onChange,
  disabled,
  hasError,
  errorText,
  hideLabel,
}) => {
  const type = getFieldType(fieldKey, value, hint);
  const label = getFieldLabel(fieldKey);
  const status = hasError ? 'error' : '';

  if (type === 'switch') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: '32px',
        }}
      >
        {!hideLabel && (
          <span style={{ ...labelStyle, marginBottom: 0 }}>{label}</span>
        )}
        <Switch checked={!!value} onChange={onChange} disabled={disabled} />
      </div>
    );
  }

  let control = null;
  if (type === 'number') {
    control = (
      <InputNumber
        value={typeof value === 'number' ? value : null}
        onChange={onChange}
        placeholder={`Enter ${label}`}
        disabled={disabled}
        min={0}
        max={isOrderKey(fieldKey) ? 99 : undefined}
        maxLength={isOrderKey(fieldKey) ? 2 : undefined}
        precision={0}
        parser={(val) => (val ? val.replace(/[^0-9]/g, '') : '')}
        onKeyPress={(e) => {
          if (!/[0-9]/.test(e.key)) {
            e.preventDefault();
          }
        }}
        style={{ width: '100%' }}
        status={status}
      />
    );
  } else if (type === 'textarea') {
    control = (
      <Input.TextArea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${label}`}
        disabled={disabled}
        rows={2}
        status={status}
      />
    );
  } else {
    control = (
      <Input
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${label}`}
        disabled={disabled}
        status={status}
      />
    );
  }

  return (
    <>
      {!hideLabel && <div style={labelStyle}>{label}</div>}
      {control}
      {errorText && (
        <div style={{ marginTop: '4px', fontSize: '12px', color: '#ff4d4f' }}>
          {errorText}
        </div>
      )}
    </>
  );
};

const FeatureUiConfigModalInput = ({ value, onChange, disabled }) => {
  const {
    state: { isDesktop },
  } = useContext(AppContext);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tempConfig, setTempConfig] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [lastAddedPath, setLastAddedPath] = useState(null);

  const parseValue = (val) => {
    if (val && typeof val === 'string' && val.trim() !== '') {
      try {
        const parsedObj = JSON.parse(val);
        return isPlainObject(parsedObj) ? parsedObj : {};
      } catch (e) {
        return {};
      }
    }
    return isPlainObject(val) ? val : {};
  };

  const getLeafFields = () =>
    collectLeafFields(tempConfig, 'uiConfig', null, undefined);

  const hasEmptyFields = () =>
    getLeafFields().some(
      ({ key, value: val, hint }) =>
        isRequiredField(key, val, hint) && isEmptyValue(val),
    );

  const hasDuplicateOrders = () =>
    getLeafFields().some(
      ({ key, value: val, list }) =>
        isOrderKey(key) && isDuplicateInList(list, key, val),
    );

  const hasInvalidUrls = () =>
    getLeafFields().some(
      ({ key, value: val, hint }) =>
        getFieldType(key, val, hint) === 'url' && isInvalidUrl(val),
    );

  const isConfigEmpty = () => Object.keys(tempConfig).length === 0;

  const isConfigInvalid = () =>
    isConfigEmpty() ||
    hasEmptyFields() ||
    hasDuplicateOrders() ||
    hasInvalidUrls();

  const getFieldIssues = () => {
    const issues = [];
    getLeafFields().forEach(({ key, value: val, list, hint }) => {
      if (!getFieldError(key, val, list, hint).hasError) return;
      const label = getFieldLabel(key);
      if (isRequiredField(key, val, hint) && isEmptyValue(val)) {
        issues.push(`"${label}" is required.`);
      } else if (getFieldType(key, val, hint) === 'url' && isInvalidUrl(val)) {
        issues.push(`"${label}" is not a valid image URL: ${val}`);
      } else if (isOrderKey(key) && isDuplicateInList(list, key, val)) {
        issues.push(
          `"${label}" ${val} is used more than once, it must be unique.`,
        );
      }
    });
    return [...new Set(issues)];
  };

  const getErrorMessage = () => {
    if (isConfigEmpty()) {
      return 'The configuration is empty, add at least one field.';
    }
    if (hasEmptyFields()) {
      return 'Please fill in all required fields.';
    }
    if (hasDuplicateOrders()) {
      return 'Feature Order must be unique for each feature configuration.';
    }
    if (hasInvalidUrls()) {
      return 'Invalid image URL.';
    }
    return null;
  };

  const handleOpenDrawer = () => {
    setTempConfig(parseValue(value));
    setShowErrors(false);
    setIsJsonMode(false);
    setJsonError(null);
    setIsDrawerOpen(true);
  };

  const handleToggleJsonMode = () => {
    setShowErrors(false);
    if (!isJsonMode) {
      setJsonDraft(JSON.stringify(tempConfig, null, 2));
      setJsonError(null);
    }
    setIsJsonMode((prev) => !prev);
  };

  const handleJsonDraftChange = (next) => {
    setShowErrors(false);
    setJsonDraft(next);
    const { error, parsed } = validateJsonDraft(next);
    setJsonError(error ?? null);
    if (!error) {
      setTempConfig(parseValue(parsed));
    }
  };

  const handleSave = () => {
    if (isJsonMode) {
      const { error } = validateJsonDraft(jsonDraft);
      if (error) {
        setJsonError(error);
        setShowErrors(true);
        return;
      }
    }
    if (isConfigInvalid()) {
      setShowErrors(true);
      return;
    }
    if (onChange) {
      onChange(JSON.stringify(tempConfig, null, 2));
    }
    setIsDrawerOpen(false);
  };

  const handleCancel = () => {
    setIsDrawerOpen(false);
  };

  const handleChangeAtPath = (path, val) => {
    setShowErrors(false);
    setTempConfig((prev) => setValueAtPath(prev, path, val));
  };

  const handleRemoveAtPath = (path) => {
    setShowErrors(false);
    setLastAddedPath(null);
    setTempConfig((prev) => removeValueAtPath(prev, path));
  };

  const handleAddItem = (path) => {
    setShowErrors(false);
    setTempConfig((prev) => {
      const list = path.reduce((acc, step) => acc?.[step], prev);
      const template = buildItemTemplate(list);
      if (template === null) return prev;
      const nextItem = isPlainObject(template) ? { ...template } : template;
      if (isPlainObject(nextItem)) {
        Object.keys(nextItem).forEach((key) => {
          if (isOrderKey(key)) {
            nextItem[key] = list.length + 1;
          }
        });
      }
      setLastAddedPath([...path, list.length].join('.'));
      return setValueAtPath(prev, path, [...list, nextItem]);
    });
  };

  const renderLeaf = (path, key, val, list, hideLabel, hint) => {
    const error = getFieldError(key, val, list, hint);
    return (
      <LeafField
        fieldKey={key}
        value={val}
        hint={hint}
        onChange={(next) => handleChangeAtPath(path, next)}
        disabled={disabled}
        hasError={showErrors && error.hasError}
        errorText={showErrors ? error.text : null}
        hideLabel={hideLabel}
      />
    );
  };

  const renderObjectFields = (obj, path, depth, keys, list) => {
    const sample = keys ? buildUnionSample([obj, ...(list ?? [])]) : obj;
    return (
      <Row gutter={[12, 12]}>
        {orderedKeys(sample, keys).map((key) => {
          const val = itemValueFor(obj, key, sample);
          const complex = isBranch(val);
          const span = complex ? 24 : 12;
          return (
            <Col xs={24} sm={span} key={key}>
              {/* eslint-disable-next-line no-use-before-define */}
              {renderNode([...path, key], key, val, list, depth, sample?.[key])}
            </Col>
          );
        })}
      </Row>
    );
  };

  const renderObjectCard = (path, key, obj, depth) => (
    <CollapsibleBox title={getFieldLabel(key)} background="#fff">
      {renderObjectFields(obj, path, depth + 1)}
    </CollapsibleBox>
  );

  const renderList = (path, key, list, depth) => {
    const itemTitle = getSectionItemTitle(key);
    const canAddItem = buildItemTemplate(list) !== null;
    const isValueList = list.length > 0 && !list.some(isPlainObject);

    const items = list.map((item, index) => {
      const itemPath = [...path, index];
      if (!isPlainObject(item)) {
        return (
          <Col xs={24} sm={12} key={index}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderLeaf(itemPath, key, item, list, true, list[0])}
              </div>
              {!disabled && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveAtPath(itemPath)}
                  size="small"
                />
              )}
            </div>
          </Col>
        );
      }

      const isCardInvalid =
        showErrors &&
        collectLeafFields(item, key, list).some(
          ({ key: leafKey, value: leafVal, list: leafList }) =>
            getFieldError(leafKey, leafVal, leafList).hasError,
        );

      return (
        <CollapsibleBox
          key={index}
          title={`${itemTitle} #${index + 1}`}
          background="#fafafa"
          invalid={isCardInvalid}
          style={{ marginBottom: '16px' }}
          defaultOpen={lastAddedPath === itemPath.join('.')}
          extra={
            !disabled && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveAtPath(itemPath);
                }}
              />
            )
          }
        >
          {renderObjectFields(
            item,
            itemPath,
            depth + 1,
            orderedKeys(buildUnionSample(list)),
            list,
          )}
        </CollapsibleBox>
      );
    });

    const body = (
      <>
        {list.length === 0 && (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: '#8c8c8c',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px',
              background: '#fafafa',
              marginBottom: '16px',
            }}
          >
            No {itemTitle.toLowerCase()} configured yet.
            {!canAddItem && !disabled && (
              <div style={{ marginTop: '4px', fontSize: '12px' }}>
                There is nothing to copy a shape from, use Edit JSON to add the
                first entry.
              </div>
            )}
          </div>
        )}

        {isValueList ? <Row gutter={[12, 12]}>{items}</Row> : items}

        {!disabled && canAddItem && (
          <div
            style={{
              textAlign: 'center',
              marginTop: '12px',
              marginBottom: '12px',
            }}
          >
            <Button
              type="dashed"
              onClick={() => handleAddItem(path)}
              icon={<PlusOutlined />}
            >
              {list.length === 0
                ? `Add ${itemTitle}`
                : `Add Another ${itemTitle}`}
            </Button>
          </div>
        )}
      </>
    );

    if (depth > 0) {
      return (
        <CollapsibleBox title={humanizeKey(key)} background="#fff">
          {body}
        </CollapsibleBox>
      );
    }

    return (
      <div style={{ marginTop: '8px' }}>
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            background: '#f5f5f5',
          }}
        >
          <Divider
            orientation="left"
            orientationMargin={0}
            style={{ margin: '8px 0 20px', borderColor: '#9264a8' }}
          >
            <span
              style={{ fontWeight: 'bold', fontSize: '15px', color: '#9264a8' }}
            >
              {humanizeKey(key)}
            </span>
          </Divider>
        </div>
        {body}
      </div>
    );
  };

  const renderNode = (path, key, val, list, depth, hint) => {
    if (isPlainObject(val)) return renderObjectCard(path, key, val, depth);
    if (Array.isArray(val)) return renderList(path, key, val, depth);
    return renderLeaf(path, key, val, list, false, hint);
  };

  const jsonIssues = isJsonMode && showErrors ? getFieldIssues() : [];

  const drawerFooter = (
    <>
      {showErrors && !jsonError && isConfigInvalid() && (
        <div
          style={{ color: '#ff4d4f', marginBottom: '8px', fontSize: '13px' }}
        >
          {getErrorMessage()}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        {disabled ? (
          <Button onClick={handleCancel}>Close</Button>
        ) : (
          <>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      <EditIcon
        className="svg-icon"
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={disabled ? undefined : handleOpenDrawer}
      />

      <Drawer
        title={
          <div className="header-text">
            <span className="header-title">
              {disabled
                ? 'View Feature UI Configuration'
                : 'Edit Feature UI Configuration'}
            </span>
          </div>
        }
        placement="right"
        open={isDrawerOpen}
        onClose={handleCancel}
        width={!isDesktop ? '100%' : '85%'}
        className="custom-drawer"
        destroyOnClose
        // eslint-disable-next-line no-undef
        getContainer={() => document.querySelector('.site-layout') || document.body}
        extra={
          <div className="drawer-header-actions">
            <Button
              className="ui-config-json-btn"
              size="middle"
              icon={isJsonMode ? <ArrowLeftOutlined /> : <EditOutlined />}
              onClick={handleToggleJsonMode}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {isJsonMode ? 'Back' : disabled ? 'View JSON' : 'Edit as JSON'}
            </Button>
          </div>
        }
        footer={drawerFooter}
      >
        <div
          className="no-scrollbar"
          style={
            isJsonMode
              ? {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : { height: '100%', overflowY: 'auto', overflowX: 'hidden' }
          }
        >
          {isJsonMode ? (
            <>
              <div style={{ ...labelStyle, marginBottom: '8px' }}>
                {disabled
                  ? 'Full uiConfig JSON.'
                  : 'Paste the full uiConfig JSON to edit every field at once.'}
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <JsonCodeEditor
                  value={jsonDraft}
                  onChange={handleJsonDraftChange}
                  readOnly={disabled}
                  config={tempConfig}
                  hasError={Boolean(jsonError)}
                />
              </div>
              {jsonError && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#ff4d4f',
                  }}
                >
                  {jsonError}
                </div>
              )}
              {!jsonError && showErrors && jsonIssues.length > 0 && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    color: '#ff4d4f',
                    maxHeight: '120px',
                    overflowY: 'auto',
                  }}
                >
                  {jsonIssues.slice(0, 8).map((issue) => (
                    <div key={issue}>• {issue}</div>
                  ))}
                  {jsonIssues.length > 8 && (
                    <div>• and {jsonIssues.length - 8} more.</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {Object.keys(tempConfig).length === 0 && (
                <div
                  style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: '#8c8c8c',
                    border: '1px dashed #d9d9d9',
                    borderRadius: '6px',
                    background: '#fafafa',
                  }}
                >
                  This plan has no feature UI configuration yet.
                  {!disabled && (
                    <div style={{ marginTop: '4px', fontSize: '12px' }}>
                      Use Edit JSON to paste one, the fields are built from it.
                    </div>
                  )}
                </div>
              )}
              {renderObjectFields(tempConfig, [], 0)}
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default FeatureUiConfigModalInput;
